import {
  CHARGE_FAILURE_CATEGORY,
  CHARGE_FAILURE_STATUS,
  formatParisDateTime,
  type ChargeFailureListItem,
} from '@flux/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchChargeFailures } from './api.js';
import { ChargeFailureDetailSheet } from './charge-failure-detail-sheet.js';
import {
  ChargeFailureFilters,
  CLEAR_CHARGE_FAILURE_FILTERS,
  hasActiveChargeFailureFilters,
  readChargeFailureFilters,
} from './charge-failure-filters.js';
import {
  chargeFailureAmount,
  chargeFailureAttempt,
  chargeFailureError,
  chargeFailureNextRetry,
} from './charge-failure-format.js';
import { useDetailSheetParam } from './detail-sheet.js';
import {
  isRechargeTokenMissing,
  RECHARGE_NOT_CONFIGURED_EMPTY_STATE,
  RechargeModeBanner,
  useRechargeSummary,
} from './recharge-status.js';

const PAGE_SIZE = 50;
/** Triables côté serveur (`apps/server/src/http/api/recharge/charge-failures.ts::SORT_FIELDS`). */
const SORTABLE_COLUMNS = ['receivedAt', 'email', 'category', 'attempt', 'retryDate', 'status'] as const;
const NUMERIC_COLUMNS = ['amount'] as const;
const DEFAULT_SORT = 'receivedAt';

function Dash(): JSX.Element {
  return <span className="text-muted-foreground">—</span>;
}

const helper = createDataTableColumnHelper<ChargeFailureListItem>();

const columns = helper.columns([
  helper.accessor('receivedAt', {
    id: 'receivedAt',
    header: 'Date',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatParisDateTime(new Date(info.getValue()))}
      </span>
    ),
  }),
  helper.accessor('email', {
    id: 'email',
    header: 'Client',
    cell: (info) => <span className="break-all">{info.getValue()}</span>,
  }),
  helper.display({
    id: 'amount',
    header: 'Montant',
    cell: ({ row }) => {
      const amount = chargeFailureAmount(row.original);
      return amount === null ? <Dash /> : <span className="whitespace-nowrap tabular-nums">{amount}</span>;
    },
  }),
  helper.display({
    id: 'error',
    header: 'Erreur',
    // Tronquée dans le tableau ; le texte complet reste dans `title` et dans le panneau.
    cell: ({ row }) => {
      const message = chargeFailureError(row.original);
      return (
        <span className="block max-w-xs truncate" title={message}>
          {message}
        </span>
      );
    },
  }),
  helper.accessor('category', {
    id: 'category',
    header: 'Catégorie',
    cell: (info) => <StatusBadge registry={CHARGE_FAILURE_CATEGORY} code={info.getValue()} />,
  }),
  helper.display({
    id: 'attempt',
    header: 'Tentative',
    cell: ({ row }) => {
      const attempt = chargeFailureAttempt(row.original);
      return attempt === null ? <Dash /> : <span className="tabular-nums">{attempt}</span>;
    },
  }),
  helper.display({
    id: 'retryDate',
    header: 'Prochaine tentative',
    cell: ({ row }) => {
      const next = chargeFailureNextRetry(row.original);
      return next === null ? <Dash /> : <span className="whitespace-nowrap tabular-nums">{next}</span>;
    },
  }),
  helper.accessor('status', {
    id: 'status',
    header: 'Statut',
    cell: (info) => <StatusBadge registry={CHARGE_FAILURE_STATUS} code={info.getValue()} />,
  }),
]);

/**
 * Onglet Paiements échoués de l'écran Abonnements (`06` §8.1), onglet par
 * défaut : renouvellements refusés, en lecture seule. Filtres, tri et page
 * dans l'URL, détail dans un panneau porté par `?id=`.
 */
export function ChargeFailuresTab(): JSX.Element {
  const rawSearch: unknown = useSearch({ strict: false });
  const { page, sortBy, sortDir, setPage, setSort, setFilters } = useDataTableUrlState();
  const { id, openDetail, closeDetail } = useDetailSheetParam();
  const summary = useRechargeSummary();
  const filters = readChargeFailureFilters(rawSearch);
  const isFiltered = hasActiveChargeFailureFilters(filters);

  const listParams = {
    page,
    pageSize: PAGE_SIZE,
    sortDir,
    ...(sortBy ? { sortBy } : {}),
    ...(filters.category.length > 0 ? { category: filters.category } : {}),
    ...(filters.status.length > 0 ? { status: filters.status } : {}),
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.subscriptions.chargeFailures(listParams),
    queryFn: () => fetchChargeFailures(listParams),
    placeholderData: keepPreviousData,
  });

  function resetFilters(): void {
    setFilters(CLEAR_CHARGE_FAILURE_FILTERS);
  }

  return (
    <>
      <RechargeModeBanner feature="chargeFailed" />
      <DataTable
        caption="Paiements échoués des renouvellements"
        columns={columns}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        sortBy={sortBy ?? DEFAULT_SORT}
        sortDir={sortDir}
        sortableColumnIds={SORTABLE_COLUMNS}
        numericColumnIds={NUMERIC_COLUMNS}
        onPageChange={setPage}
        onSortChange={setSort}
        onRowClick={(row) => {
          openDetail(row.id);
        }}
        isLoading={query.isPending}
        isError={query.isError}
        errorMessage={
          query.error instanceof ApiError
            ? query.error.message
            : 'Impossible de charger les paiements échoués.'
        }
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        toolbar={<ChargeFailureFilters value={filters} onChange={setFilters} />}
        emptyState={
          isFiltered
            ? {
                title: 'Aucun résultat pour ces filtres',
                description: 'Élargissez la période, modifiez la recherche ou retirez un filtre.',
                action: (
                  <Button type="button" variant="outline" onClick={resetFilters}>
                    Réinitialiser les filtres
                  </Button>
                ),
              }
            : isRechargeTokenMissing(summary.data)
              ? RECHARGE_NOT_CONFIGURED_EMPTY_STATE
              : {
                  title: 'Aucun paiement échoué',
                  description: 'Les renouvellements refusés par la banque apparaîtront ici.',
                }
        }
      />
      <ChargeFailureDetailSheet id={id} onClose={closeDetail} />
    </>
  );
}
